import React from 'react'
import { PostHogProvider, usePostHog } from 'posthog-js/react'
import useCurrentUser from '../../hooks/auth/useCurrentUser'
import { parseSubdomain } from '../../helpers/stringUtils'
import features from './features'
import { POSTHOG_API_HOST } from '../../constants'
type FeatureContextType = {
  isFeatureEnabled: (featureName: string) => boolean
}

const FeatureContext = React.createContext<FeatureContextType>({
  isFeatureEnabled: () => false,
})

// Component to identify users and handle feature flags
const FeatureInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const posthog = usePostHog()
  const { currentUser } = useCurrentUser()
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = React.useState(false)
  const subdomain = React.useMemo(() => parseSubdomain(window.location.hostname), [])

  React.useEffect(() => {
    posthog.identify(currentUser !== undefined ? currentUser.id : 'anonymous', {
      email: currentUser?.email,
      name: currentUser?.name,
      subdomain: subdomain,
    })
  }, [currentUser, posthog, subdomain])

  React.useEffect(() => {
    return posthog.onFeatureFlags(() => {
      setFeatureFlagsLoaded(true)
    })
  }, [posthog])

  const isFeatureEnabled = React.useCallback(
    (featureName: string) => {
      return featureFlagsLoaded && (posthog?.isFeatureEnabled(featureName) ?? false)
    },
    [featureFlagsLoaded, posthog],
  )

  return <FeatureContext.Provider value={{ isFeatureEnabled }}>{children}</FeatureContext.Provider>
}

type FeatureProviderProps = {
  children: React.ReactNode
}

const FeatureProvider: React.FC<FeatureProviderProps> = ({ children }) => {
  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY || ''}
      options={{
        api_host: POSTHOG_API_HOST,
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') posthog.debug()
        },
      }}
    >
      <FeatureInitializer>{children}</FeatureInitializer>
    </PostHogProvider>
  )
}

export const useFeatures = () => {
  const context = React.useContext(FeatureContext)
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider')
  }

  return {
    [features.nodaFiCheck]: context.isFeatureEnabled(features.nodaFiCheck),
    [features.survey]: context.isFeatureEnabled(features.survey),
    [features.externalOrganizations]: context.isFeatureEnabled(features.externalOrganizations),
    [features.insight]: context.isFeatureEnabled(features.insight),
    [features.procedures]: context.isFeatureEnabled(features.procedures),
    [features.purchaseOrders]: context.isFeatureEnabled(features.purchaseOrders),
    [features.issueCategoryHierarchy]: context.isFeatureEnabled(features.issueCategoryHierarchy),
    [features.duplicatePmsConfig]: context.isFeatureEnabled(features.duplicatePmsConfig),
    [features.advancedInsights]: context.isFeatureEnabled(features.advancedInsights),
    [features.duplicateLocationConfig]: context.isFeatureEnabled(features.duplicateLocationConfig),
    [features.pmPlanner]: context.isFeatureEnabled(features.pmPlanner),
    [features.goldenBroom]: context.isFeatureEnabled(features.goldenBroom),
    [features.aiReports]: context.isFeatureEnabled(features.aiReports),
    [features.archivedTickets]: context.isFeatureEnabled(features.archivedTickets),
    [features.integrationsTab]: context.isFeatureEnabled(features.integrationsTab),
    [features.procedureEntryStatsTableWeb]: context.isFeatureEnabled(
      features.procedureEntryStatsTableWeb,
    ),
    [features.aiProcedureTemplateGenerator]: context.isFeatureEnabled(
      features.aiProcedureTemplateGenerator,
    ),
  }
}

export default FeatureProvider
